import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, Chip, 
  List, ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Switch,
  Tooltip, Alert, CircularProgress, Divider, Badge, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Avatar
} from '@mui/material';
import {
  People as PeopleIcon,
  CreditCard as CardIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as SyncedIcon,
  Error as UnsyncedIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon,
  Security as SecurityIcon,
  PersonAdd as PersonAddIcon,
  Block as BlockIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import axiosInstance from '../axiosConfig';
import moment from 'moment';

const EmployeeIntegration = () => {
  const [employees, setEmployees] = useState([]);
  const [unifiCards, setUnifiCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'assign', 'permissions', 'sync'
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle', 'syncing', 'completed', 'error'

  useEffect(() => {
    fetchEmployees();
    fetchUnifiCards();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/projects/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnifiCards = async () => {
    try {
      const response = await axiosInstance.get('/api/unifi/access/cards');
      setUnifiCards(response.data);
    } catch (error) {
      console.error('Error fetching UniFi cards:', error);
    }
  };

  const handleSyncEmployees = async () => {
    try {
      setSyncStatus('syncing');
      await axiosInstance.post('/api/unifi/access/sync-employees');
      setSyncStatus('completed');
      fetchEmployees();
      fetchUnifiCards();
      showSnackbar('Sincronización completada', 'success');
    } catch (error) {
      console.error('Error syncing employees:', error);
      setSyncStatus('error');
      showSnackbar('Error en la sincronización', 'error');
    }
  };

  const handleAssignCard = async (employeeId, cardId) => {
    try {
      await axiosInstance.post('/api/unifi/access/assign-card', {
        employeeId,
        cardId
      });
      showSnackbar('Tarjeta asignada exitosamente', 'success');
      fetchEmployees();
      fetchUnifiCards();
    } catch (error) {
      console.error('Error assigning card:', error);
      showSnackbar('Error al asignar tarjeta', 'error');
    }
  };

  const handleRevokeAccess = async (employeeId) => {
    try {
      await axiosInstance.post('/api/unifi/access/revoke-access', {
        employeeId
      });
      showSnackbar('Acceso revocado exitosamente', 'success');
      fetchEmployees();
    } catch (error) {
      console.error('Error revoking access:', error);
      showSnackbar('Error al revocar acceso', 'error');
    }
  };

  const handleUpdatePermissions = async (employeeId, permissions) => {
    try {
      await axiosInstance.post('/api/unifi/access/update-permissions', {
        employeeId,
        permissions
      });
      showSnackbar('Permisos actualizados', 'success');
      fetchEmployees();
    } catch (error) {
      console.error('Error updating permissions:', error);
      showSnackbar('Error al actualizar permisos', 'error');
    }
  };

  const handleOpenDialog = (type, employee = null) => {
    setDialogType(type);
    setSelectedEmployee(employee);
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setSelectedEmployee(null);
    setDialogType('');
  };

  const showSnackbar = (message, severity = 'success') => {
    console.log(`${severity}: ${message}`);
  };

  const getSyncStatusColor = (status) => {
    switch (status) {
      case 'synced': return 'success';
      case 'unsynced': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getSyncStatusText = (status) => {
    switch (status) {
      case 'synced': return 'Sincronizado';
      case 'unsynced': return 'No Sincronizado';
      case 'pending': return 'Pendiente';
      default: return 'Desconocido';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Integración de Empleados
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Sincronizar con UniFi">
            <IconButton 
              onClick={handleSyncEmployees} 
              disabled={syncStatus === 'syncing'}
              color={syncStatus === 'completed' ? 'success' : 'default'}
            >
              <SyncIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Actualizar">
            <IconButton onClick={() => { fetchEmployees(); fetchUnifiCards(); }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            Agregar Empleado
          </Button>
        </Box>
      </Box>

      {/* Sync Status */}
      {syncStatus === 'syncing' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Sincronizando empleados con UniFi Access...
        </Alert>
      )}
      {syncStatus === 'completed' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Sincronización completada exitosamente
        </Alert>
      )}
      {syncStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error en la sincronización. Intente nuevamente.
        </Alert>
      )}

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', bgcolor: 'primary.light', color: 'white' }}>
            <CardContent>
              <PeopleIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">{employees.length}</Typography>
              <Typography variant="body2">Total Empleados</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', bgcolor: 'success.light', color: 'white' }}>
            <CardContent>
              <SyncedIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">
                {employees.filter(emp => emp.syncStatus === 'synced').length}
              </Typography>
              <Typography variant="body2">Sincronizados</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', bgcolor: 'warning.light', color: 'white' }}>
            <CardContent>
              <UnsyncedIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">
                {employees.filter(emp => emp.syncStatus === 'unsynced').length}
              </Typography>
              <Typography variant="body2">Pendientes</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', bgcolor: 'info.light', color: 'white' }}>
            <CardContent>
              <CardIcon sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">{unifiCards.length}</Typography>
              <Typography variant="body2">Tarjetas Disponibles</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Employees Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Empleados y Acceso
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Empleado</TableCell>
                  <TableCell>Tarjeta Asignada</TableCell>
                  <TableCell>Estado Sincronización</TableCell>
                  <TableCell>Permisos de Acceso</TableCell>
                  <TableCell>Último Acceso</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar src={employee.photo_url} sx={{ width: 32, height: 32 }}>
                          <PeopleIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {employee.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {employee.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {employee.assignedCard ? (
                        <Chip
                          icon={<CardIcon />}
                          label={employee.assignedCard}
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          label="Sin tarjeta"
                          color="default"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={employee.syncStatus === 'synced' ? <SyncedIcon /> : <UnsyncedIcon />}
                        label={getSyncStatusText(employee.syncStatus)}
                        color={getSyncStatusColor(employee.syncStatus)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {employee.accessPermissions?.map((permission, index) => (
                          <Chip
                            key={index}
                            label={permission}
                            size="small"
                            variant="outlined"
                          />
                        )) || (
                          <Typography variant="caption" color="text.secondary">
                            Sin permisos
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {employee.lastAccess ? (
                        <Typography variant="caption">
                          {moment(employee.lastAccess).format('DD/MM/YYYY HH:mm')}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Nunca
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Asignar tarjeta">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog('assign', employee)}
                            disabled={employee.syncStatus === 'synced'}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Gestionar permisos">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenDialog('permissions', employee)}
                          >
                            <SecurityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Revocar acceso">
                          <IconButton 
                            size="small" 
                            onClick={() => handleRevokeAccess(employee.id)}
                            color="error"
                          >
                            <BlockIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Available Cards */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Tarjetas Disponibles
          </Typography>
          <Grid container spacing={2}>
            {unifiCards.filter(card => !card.assigned).map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <CardIcon color="primary" />
                      <Typography variant="body2" fontWeight="medium">
                        {card.name}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      ID: {card.id}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Chip
                        label="Disponible"
                        color="success"
                        size="small"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Assign Card Dialog */}
      <Dialog open={openDialog && dialogType === 'assign'} onClose={closeDialog}>
        <DialogTitle>Asignar Tarjeta - {selectedEmployee?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Seleccione una tarjeta disponible para asignar a este empleado:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Tarjeta</InputLabel>
            <Select>
              {unifiCards.filter(card => !card.assigned).map((card) => (
                <MenuItem 
                  key={card.id} 
                  value={card.id}
                  onClick={() => handleAssignCard(selectedEmployee.id, card.id)}
                >
                  {card.name} (ID: {card.id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={openDialog && dialogType === 'permissions'} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>Gestionar Permisos - {selectedEmployee?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure qué puertas puede acceder este empleado:
          </Typography>
          <Grid container spacing={2}>
            {['Entrada Principal', 'Oficinas', 'Almacén', 'Sala de Reuniones', 'Estacionamiento'].map((door) => (
              <Grid item xs={12} sm={6} key={door}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Switch 
                    defaultChecked={selectedEmployee?.accessPermissions?.includes(door)}
                  />
                  <Typography variant="body2">{door}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button variant="contained">Guardar Permisos</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmployeeIntegration; 